import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Footprints, Trophy, Flag, Compass, Sailboat, Waves, Bike,
  ShoppingBasket, Utensils, Baby, Check, MapPin,
} from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";

const ACT_ICONS = [Footprints, Trophy, Flag, Compass, Sailboat, Waves, Bike, ShoppingBasket, Utensils, Baby];
const GR34_IMG = "https://images.unsplash.com/photo-1775438817482-dcb8b581cf70?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ITIRANDO = "/api/files/begaudiere/uploads/5c10c315-223d-46b2-a35b-c3ffc36c2973.jpg";

export default function RegionPage() {
  const { t } = useI18n();
  const { settings } = useSite();
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/region").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const activities = t("regionPage.activities");

  return (
    <div>
      <PageHero title={t("regionPage.title")} subtitle={t("regionPage.subtitle")} image="https://images.unsplash.com/photo-1560087389-660db08335c3?q=85&w=1600" crumb={t("nav.region")} />

      {/* Intro */}
      <section className="pt-20 lg:pt-28 pb-4">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal><p className="text-ink-600 font-body text-lg leading-relaxed">{t("regionPage.intro")}</p></Reveal>
          <Reveal delay={120}>
            <div className="mt-8 flex flex-col items-center gap-2" data-testid="region-label-bretagne">
              <img src="/tourisme-bretagne.png" alt="Label Tourisme Bretagne" loading="lazy" decoding="async" className="h-11 w-auto" />
              <span className="text-xs text-ink-400">{t("regionPage.labelText")}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GR 34 highlight */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden bg-brand-900 text-white grid lg:grid-cols-2">
            <div className="relative min-h-[280px] lg:min-h-full">
              <img src={GR34_IMG} alt="GR 34, sentier des douaniers" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              <img src={ITIRANDO} alt="Étape itirando.bzh, Sentier des Douaniers, Bretagne" loading="lazy" decoding="async" className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 w-24 sm:w-28 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.3)] ring-4 ring-white/90" data-testid="itirando-badge" />
            </div>
            <Reveal className="p-8 sm:p-12 lg:p-14">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium tracking-wide text-brand-100 mb-5">
                <Footprints size={15} strokeWidth={1.75} /> {t("regionPage.gr34Tag")}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-5">{t("regionPage.gr34Title")}</h2>
              <div className="space-y-4 text-white/80 font-body leading-relaxed">
                <p>{t("regionPage.gr34P1")}</p>
                <p>{t("regionPage.gr34P2")}</p>
              </div>
              <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white/90">
                <Footprints size={15} className="text-brand-200" /> {t("regionPage.etapeText")}
              </p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {t("regionPage.gr34Points").map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-sm text-white/90">
                    <Check size={17} className="text-brand-200 shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/disponibilites" className="inline-flex items-center gap-2 rounded-full bg-white text-brand-900 px-6 py-3 font-medium hover:bg-brand-50 transition-colors" data-testid="gr34-book">
                  {t("common.bookStay")} <ArrowRight size={17} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-6 py-3 font-medium hover:bg-white/10 transition-colors">
                  {t("common.contactDomain")}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Activities */}
      <section className="py-16 lg:py-24 bg-beige/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-brand font-accent text-2xl italic mb-2">{t("regionPage.activitiesSubtitle")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900">{t("regionPage.activitiesTitle")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {activities.map(([label, desc, dist], i) => {
              const Icon = ACT_ICONS[i % ACT_ICONS.length];
              return (
                <Reveal key={label} delay={(i % 5) * 70}>
                  <div className="bg-white rounded-2xl border border-black/5 p-6 h-full flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center mb-4"><Icon size={22} strokeWidth={1.5} className="text-brand" /></div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="font-heading text-xl text-ink-900">{label}</h3>
                      {dist ? <span className="rounded-full bg-brand-50 text-brand text-[11px] font-medium px-2 py-0.5">{dist}</span> : null}
                    </div>
                    <p className="text-ink-600 font-body text-sm leading-relaxed">{desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl text-ink-900 mb-3">{t("regionPage.destinationsTitle")}</h2>
            <p className="text-ink-600 font-body flex items-start gap-2"><MapPin size={18} className="text-brand shrink-0 mt-1" /> {t("regionPage.nearbyText")}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 90}>
                <Link to={`/la-region/${r.slug}`} className="group block bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)] h-full" data-testid={`region-card-${r.slug}`}>
                  <div className="relative aspect-[3/2] overflow-hidden">
                    <img src={r.main_image} alt={r.name} loading="lazy" className="img-zoom w-full h-full object-cover" />
                    {r.distance ? (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 text-brand px-3 py-1.5 text-xs font-medium shadow-sm" data-testid={`region-distance-${r.slug}`}>
                        <MapPin size={13} /> {r.distance}
                      </span>
                    ) : null}
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-2xl text-ink-900 mb-2">{r.name}</h3>
                    <p className="text-ink-600 font-body text-sm leading-relaxed line-clamp-2 mb-3">{r.description}</p>
                    {Array.isArray(r.highlights) && r.highlights.length > 0 && (
                      <ul className="space-y-1.5 mb-4">
                        {r.highlights.slice(0, 2).map((h) => (
                          <li key={h} className="flex items-start gap-2 text-xs text-ink-500 leading-snug"><Compass size={13} className="text-brand shrink-0 mt-0.5" /> <span className="line-clamp-1">{h}</span></li>
                        ))}
                      </ul>
                    )}
                    <span className="inline-flex items-center gap-2 text-brand font-medium text-sm group-hover:gap-3 transition-all">{t("common.seeMore")} <ArrowRight size={16} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-ink-900 mb-4">{t("regionPage.ctaTitle")}</h2>
            <p className="text-ink-600 font-body text-lg mb-8">{t("regionPage.ctaText")}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/disponibilites" className="rounded-full bg-brand text-white px-7 py-3.5 font-medium hover:bg-brand-600 transition-colors">{t("common.seeAvailability")}</Link>
              <Link to="/chambres-hotes" className="rounded-full border border-brand text-brand px-7 py-3.5 font-medium hover:bg-brand-50 transition-colors">{t("common.allRooms")}</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
